import { useMemo } from "react"

import { useScheduledRunsListTransport } from "@/runs/scheduled"
import { RefreshCw } from "lucide-react"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { isFilterSectionDisabled } from "@/components/filters/filterSectionVisibility"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"

import { useRunsFilter } from "../../contexts/RunsFilterContext"
import type { FilterSectionProps } from "../../types/filters"

export const ScheduledJobFilterSection: React.FC<FilterSectionProps> = ({
  isOpen,
  onToggle,
  filterEnabled,
}) => {
  const { scheduledJobFilters, setScheduledJobFilters } = useRunsFilter()
  const {
    data: scheduledRuns,
    isError,
    isLoading,
  } = useScheduledRunsListTransport({ enabled: filterEnabled !== false })
  const scheduledRunItems = scheduledRuns?.scheduled_runs

  const items = useMemo(() => {
    if (!scheduledRunItems) {
      return []
    }
    return scheduledRunItems.map((job) => ({
      id: job.id,
      label: job.display_name || job.id,
      searchValue: job.display_name || job.id,
    }))
  }, [scheduledRunItems])

  const isDisabled = isFilterSectionDisabled({
    activeFilterCount: scheduledJobFilters.length,
    isDeferred: filterEnabled === false,
    isError,
    isLoading,
    optionCount: items.length,
  })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={isDisabled}
      emptyMessage="No scheduled runs available in this workspace."
      trigger={
        <FilterTrigger
          label="Scheduled Runs"
          icon={<RefreshCw className="w-4 h-4" />}
          isActive={scheduledJobFilters.length > 0}
          disabled={isDisabled}
        />
      }
    >
      <SearchableCheckboxList
        items={items}
        selectedIds={scheduledJobFilters}
        onSelectionChange={setScheduledJobFilters}
        isLoading={isLoading}
        isDeferred={filterEnabled === false}
        isOpen={isOpen}
        placeholder="Search scheduled jobs..."
        emptyMessage="No scheduled jobs found"
        noItemsMessage="No scheduled jobs available"
      />
    </FilterPopover>
  )
}
